import { useEffect, useState, useCallback } from "react";
import api, { errMsg } from "../lib/api";
import { PageHeader, Card, Badge, Modal, Btn, Field, inputCls } from "../components/common";
import { toast } from "sonner";
import { RefreshCw, Eye, CheckCircle2, XCircle } from "lucide-react";
import { fmtIDR, fmtDate } from "../lib/format";

const TONE = { menunggu: "amber", diterima: "emerald", ditolak: "rose" };

export default function PembayaranApp() {
  const [rows, setRows] = useState([]);
  const [status, setStatus] = useState("menunggu");
  const [pulling, setPulling] = useState(false);
  const [receiptUrl, setReceiptUrl] = useState(null);
  const [rejectFor, setRejectFor] = useState(null);
  const [rejectNote, setRejectNote] = useState("");

  const load = useCallback(() => {
    api.get("/ajl/pembayaran", { params: status ? { status } : {} }).then((r) => setRows(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [status]);
  useEffect(() => { load(); }, [load]);

  const pull = async () => {
    setPulling(true);
    try { const r = await api.post("/ajl/pull"); toast.success(r.data.skipped ? "URL AJL belum diset" : "Penarikan selesai"); load(); }
    catch (e) { toast.error(errMsg(e)); } finally { setPulling(false); }
  };

  const openReceipt = async (p) => {
    try {
      const r = await api.get(`/ajl/pembayaran/${p.id}/receipt`, { responseType: "blob" });
      setReceiptUrl(URL.createObjectURL(r.data));
    } catch { toast.error("Bukti tidak tersedia"); }
  };

  const accept = (p) => api.post(`/ajl/pembayaran/${p.id}/accept`)
    .then((r) => { toast.success(r.data.message); load(); })
    .catch((e) => toast.error(errMsg(e)));

  const reject = () => {
    if (!rejectNote.trim()) return toast.error("Alasan penolakan wajib diisi");
    api.post(`/ajl/pembayaran/${rejectFor.id}/reject`, { note: rejectNote })
      .then(() => { toast.success("Pembayaran ditolak"); setRejectFor(null); setRejectNote(""); load(); })
      .catch((e) => toast.error(errMsg(e)));
  };

  return (
    <div data-testid="pembayaran-app-page">
      <PageHeader title="Pembayaran dari Aplikasi" subtitle="Konfirmasi transfer konsumen dari aplikasi ABAD JAYA LIVING — diterima = jadwal tagihan ter-update & kwitansi terbit" testid="payapp-header">
        <Btn onClick={pull} disabled={pulling} data-testid="payapp-pull-btn"><RefreshCw className={`w-4 h-4 ${pulling ? "animate-spin" : ""}`} /> Tarik sekarang</Btn>
      </PageHeader>
      <div className="flex gap-2 mb-4 flex-wrap">
        {["menunggu", "diterima", "ditolak", ""].map((s) => (
          <button key={s} onClick={() => setStatus(s)} data-testid={`payapp-filter-${s || "semua"}`}
            className={`text-xs px-3.5 py-2 rounded-lg border transition-colors ${status === s ? "bg-emerald-600/10 text-emerald-300 border-emerald-500/50" : "text-slate-400 border-slate-800 hover:bg-slate-900"}`}>
            {s ? s[0].toUpperCase() + s.slice(1) : "Semua"}
          </button>
        ))}
      </div>
      <div className="space-y-2" data-testid="payapp-list">
        {rows.length === 0 && <Card><p className="text-slate-500 text-sm">Tidak ada pembayaran pada status ini.</p></Card>}
        {rows.map((p) => (
          <Card key={p.id} className="!p-4" testid={`payapp-${p.id}`}>
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-medium text-slate-200">{p.invoice_label || p.invoice_id}</span>
              <Badge tone={TONE[p.status] || "slate"}>{p.status}</Badge>
              <span className="text-emerald-300 font-mono text-sm">{fmtIDR(p.amount)}</span>
              <span className="text-[11px] text-slate-500">dari tagihan {fmtIDR(p.invoice_amount)} · jatuh tempo {fmtDate(p.due_date)}</span>
            </div>
            <div className="text-[11px] text-slate-500 mt-1">
              {(p.konsumen || {}).name || "-"} · {(p.konsumen || {}).email || "-"} · {p.property_label || "-"} · {p.bank_name || "-"} · transfer {fmtDate(p.transfer_date)}
              {p.note && <span className="text-rose-300"> · {p.note}</span>}
            </div>
            <div className="flex gap-2 mt-3 flex-wrap">
              <Btn onClick={() => openReceipt(p)} className="!py-1.5 !px-3 text-xs" data-testid={`payapp-receipt-${p.id}`}><Eye className="w-3.5 h-3.5" /> Bukti</Btn>
              {p.status === "menunggu" && (
                <>
                  <Btn onClick={() => accept(p)} className="!py-1.5 !px-3 text-xs" data-testid={`payapp-accept-${p.id}`}><CheckCircle2 className="w-3.5 h-3.5" /> Terima</Btn>
                  <Btn onClick={() => { setRejectFor(p); setRejectNote(""); }} className="!py-1.5 !px-3 text-xs !bg-rose-600/20 !text-rose-300" data-testid={`payapp-reject-${p.id}`}><XCircle className="w-3.5 h-3.5" /> Tolak</Btn>
                </>
              )}
            </div>
          </Card>
        ))}
      </div>

      <Modal open={!!receiptUrl} onClose={() => setReceiptUrl(null)} title="Bukti Transfer" testid="payapp-receipt-modal">
        {receiptUrl && <img src={receiptUrl} alt="bukti transfer" className="w-full rounded-lg" data-testid="payapp-receipt-img" />}
      </Modal>

      <Modal open={!!rejectFor} onClose={() => setRejectFor(null)} title={`Tolak Pembayaran — ${rejectFor?.invoice_label || ""}`} testid="payapp-reject-modal">
        <Field label="Alasan penolakan (wajib)">
          <textarea className={inputCls} rows={3} value={rejectNote} onChange={(e) => setRejectNote(e.target.value)} placeholder="Contoh: nominal tidak sesuai tagihan" data-testid="payapp-reject-note" />
        </Field>
        <Btn onClick={reject} className="mt-4 !bg-rose-600/80" data-testid="payapp-reject-submit">Tolak Pembayaran</Btn>
      </Modal>
    </div>
  );
}
